import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import apiManager from '../../../lib/api';
import { Category } from '../../../lib/api/models';
import { T, useTranslate } from '@tolgee/react';

const CategoryFormPage = () => {
  const { t } = useTranslate();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<Partial<Category>>({
    name: '',
    description: '',
    isActive: true,
    parent: undefined,
    slug: '',
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Cargar todas las categorías para el selector de categoría padre
    const fetchCategories = async () => {
      try {
        const response = await apiManager.categories.getCategories();
        if (response.success && response.data) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error('Error al cargar categorías:', error);
      }
    };

    // Cargar datos de la categoría si estamos en modo edición
    const fetchCategory = async () => {
      if (!isEditMode) return;

      setIsLoading(true);
      try {
        const response = await apiManager.categories.getCategory(id);
        if (response.success && response.data) {
          setFormData(response.data);
          if (response.data.image) {
            setImagePreview(response.data.image);
          }
        }
      } catch (error) {
        console.error('Error al cargar datos de la categoría:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
    fetchCategory();
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked
      });
    } else if (name === 'parent') {
      if (value) {
        const parentCategory = categories.find(cat => cat.id === value || (cat as any)._id === value);
        setFormData({
          ...formData,
          parent: parentCategory
        });
      } else {
        setFormData({
          ...formData,
          parent: undefined
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }

    // Limpiar error del campo
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Generar slug automáticamente si el campo de nombre cambia y no estamos en modo edición
    if (name === 'name' && !isEditMode) {
      const slug = value.toLowerCase()
        .replace(/[^\w\s-]/g, '') // Eliminar caracteres especiales
        .replace(/\s+/g, '-')     // Cambiar espacios por guiones
        .replace(/-+/g, '-');     // Eliminar guiones consecutivos

      setFormData(prev => ({
        ...prev,
        slug
      }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    setImageFile(file);

    // Generar preview
    const preview = URL.createObjectURL(file);
    setImagePreview(preview);

    // Limpiar error de imagen si existe
    if (errors.image) {
      setErrors({
        ...errors,
        image: ''
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name) newErrors.name = t('admin.category_form.error_name');
    if (!formData.slug) newErrors.slug = t('admin.category_form.error_slug');

    // Validar que no sea su propio padre
    if (isEditMode && formData.parent && formData.parent.id === id) {
      newErrors.parent = t('admin.category_form.error_parent_self');
    }

    // Validar que el padre no sea un hijo (para evitar ciclos)
    if (isEditMode && formData.parent) {
      let parentCategory = formData.parent;
      let hasLoop = false;

      // Verificar si hay un ciclo en la jerarquía
      const visited = new Set<string>();
      visited.add(id);

      while (parentCategory) {
        if (visited.has(parentCategory.id)) {
          hasLoop = true;
          break;
        }

        const nextParent = categories.find(cat => cat.id === parentCategory?.id)?.parent;
        if (!nextParent) break;

        parentCategory = nextParent;
      }

      if (hasLoop) {
        newErrors.parent = t('admin.category_form.error_parent_loop');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);

    try {
      let response;

      // Crear o actualizar la categoría
      if (isEditMode) {
        response = await apiManager.categories.updateCategory(id, formData);
      } else {
        response = await apiManager.categories.createCategory(formData);
      }

      if (response.success && response.data) {
        const categoryId = isEditMode ? id : response.data.id;

        // Subir imagen si hay una nueva
        if (imageFile) {
          await apiManager.categories.uploadCategoryImage(categoryId, imageFile);
        }

        // Redirigir a la lista de categorías
        navigate('/admin/categories');
      } else {
        // Mostrar error
        setErrors({
          submit: t('admin.category_form.error_save')
        });
      }
    } catch (error) {
      console.error('Error al guardar la categoría:', error);
      setErrors({
        submit: t('admin.category_form.error_save')
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Filtrar categorías que pueden ser padres (para evitar ciclos)
  const getAvailableParentCategories = () => {
    if (!isEditMode) return categories;

    // En modo edición, eliminar la propia categoría y sus hijos
    return categories.filter(cat => cat.id !== id);
  };

  return (
    <AdminLayout title={isEditMode ? t('admin.category_form.title_edit', 'Editar Categoría') : t('admin.category_form.title_new', 'Nueva Categoría')}>
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
              {errors.submit}
            </div>
          )}

          {/* Información básica */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              <T keyName="admin.category_form.basic_info">Información Básica</T>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.category_form.name">Nombre</T> <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.category_form.slug">Slug</T> <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.slug ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                />
                {errors.slug && <p className="mt-1 text-sm text-red-500">{errors.slug}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  <T keyName="admin.category_form.slug_help">El slug se usa en las URLs. Se genera automáticamente, pero puedes editarlo.</T>
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.category_form.description">Descripción</T>
                </label>
                <textarea
                  name="description"
                  rows={4}
                  value={formData.description || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.category_form.parent_category">Categoría padre</T>
                </label>
                <select
                  name="parent"
                  value={typeof formData.parent === 'string' ? formData.parent : (formData.parent?.id || '')}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.parent ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                >
                  <option value="">{t('admin.category_form.no_parent')}</option>
                  {getAvailableParentCategories().map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.parent && <p className="mt-1 text-sm text-red-500">{errors.parent}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => handleChange({
                    ...e,
                    target: {
                      ...e.target,
                      name: 'isActive',
                      value: e.target.checked ? 'true' : 'false',
                      type: 'checkbox'
                    }
                  })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm font-medium text-gray-700">
                  <T keyName="admin.category_form.active">Categoría activa</T>
                </label>
              </div>
            </div>
          </div>

          {/* Imagen */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              <T keyName="admin.category_form.image">Imagen de Categoría</T>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <T keyName="admin.category_form.image_label">Imagen</T>
                </label>
                <input
                  type="file"
                  onChange={handleImageChange}
                  accept="image/*"
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                {errors.image && <p className="mt-1 text-sm text-red-500">{errors.image}</p>}
              </div>

              {imagePreview && (
                <div className="flex items-center justify-center">
                  <div className="relative w-32 h-32 rounded-md overflow-hidden border border-gray-200">
                    <img
                      src={imagePreview}
                      alt={t('admin.category_form.preview')}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setImageFile(null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/admin/categories')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <T keyName="admin.category_form.cancel">Cancelar</T>
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-indigo-600 text-white rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <T keyName="admin.category_form.saving">Guardando...</T>
                </>
              ) : (
                <T keyName="admin.category_form.save">Guardar Categoría</T>
              )}
            </button>
          </div>
        </form>
      )}
    </AdminLayout>
  );
};

export default CategoryFormPage;
